import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useForm, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import {
  BatchWorkflow,
  CreateBatchWorkflowDto,
  WorkflowFlowType,
  OpenWorkflowSummary,
} from '../types/workflow';
import { workflowService } from '../services/workflowService';
import {
  DEFAULT_WORKFLOW_FLOW_TYPE,
  DEFAULT_FORM_VALUES,
  createValidationSchema,
  CreateBatchFormData,
  useWorkflowFormData,
} from '../components/Workflow/CreateBatchDialog/index';

interface UseWorkflowCreateFormOptions {
  /** Whether the form is "active" (dialog open or page mounted) */
  active: boolean;
  /** Callback when workflow is created successfully */
  onCreated?: (workflow: BatchWorkflow) => void;
  /**
   * Opt-in for the full-page `/workflow/new` view ONLY: seed `flowType`, `farmerId` and `farmId` from the
   * URL's `?flowType=&farmerId=&farmId=` once, on mount (redesign-plan §9.6 — the Farm Workspace
   * "New request here" link). `CreateBatchDialog` keeps the default (false) and never reads URL params.
   */
  seedFromSearchParams?: boolean;
}

/** What `?flowType=&farmerId=&farmId=` resolved to; every part optional. */
export interface WorkflowCreateSeed {
  flowType?: WorkflowFlowType;
  farmerId?: number;
  farmId?: number;
}

const FLOW_TYPES: readonly WorkflowFlowType[] = ['NewRequest', 'LabTriggered', 'ExistingBatch'];

const parsePositiveInt = (raw: string | null): number | undefined =>
  raw !== null && /^\d+$/.test(raw) && Number(raw) > 0 ? Number(raw) : undefined;

/**
 * Parse the create-page seed. A farm is only meaningful under its farmer, and an entity seed without an
 * explicit flow type implies `NewRequest` — the one flow whose farmer/farm fields are visible on the page
 * (the default, LabTriggered, hides them, so a seeded farm would be submitted invisibly).
 */
export function parseWorkflowCreateSeed(params: URLSearchParams): WorkflowCreateSeed {
  const flowTypeRaw = params.get('flowType');
  const explicitFlowType = FLOW_TYPES.find((type) => type === flowTypeRaw);
  const farmerId = parsePositiveInt(params.get('farmerId'));
  const farmId = farmerId ? parsePositiveInt(params.get('farmId')) : undefined;
  return {
    flowType: explicitFlowType ?? (farmerId ? 'NewRequest' : undefined),
    farmerId,
    farmId,
  };
}

/**
 * Shared hook for workflow creation form logic.
 *
 * Manages the full lifecycle of the create-workflow form:
 * - Flow type state (NewRequest vs LabTriggered)
 * - React-hook-form setup with yup validation
 * - Form data fetching via useWorkflowFormData
 * - Flow type change handling (resets flow-specific fields)
 * - Submit logic with DTO mapping and error handling
 * - Watched values (selectedFarmerId for farm filtering)
 *
 * Used by both CreateBatchDialog (dialog) and WorkflowCreateView (full page).
 */
export function useWorkflowCreateForm({ active, onCreated, seedFromSearchParams = false }: UseWorkflowCreateFormOptions) {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  // Read ONCE on mount (state initialiser) — a later URL change never re-seeds a form the user is editing.
  const [seed] = useState<WorkflowCreateSeed>(() =>
    seedFromSearchParams ? parseWorkflowCreateSeed(searchParams) : {},
  );
  // Order of seeding (§9.6): flow type first, then farmer (both before the first render), then the farm —
  // applied by the farmer-change effect below AFTER its farms-by-farmer fetch settles, because that effect
  // clears `farmId` whenever the farmer changes. Consumed once.
  const pendingSeedFarm = useRef<{ farmerId: number; farmId: number } | null>(
    seed.farmerId && seed.farmId ? { farmerId: seed.farmerId, farmId: seed.farmId } : null,
  );
  const [flowType, setFlowType] = useState<WorkflowFlowType>(seed.flowType ?? DEFAULT_WORKFLOW_FLOW_TYPE);
  const { data, loading, fetchFarmsByFarmer, resetData, addFarmer, addFarm } = useWorkflowFormData(active, flowType);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Duplicate warning state
  const [duplicates, setDuplicates] = useState<OpenWorkflowSummary[]>([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<CreateBatchFormData | null>(null);

  // Form setup with typed yup validation schema
  // Note: Type assertion needed due to yup/react-hook-form type inference mismatch
  // with nullable fields. The schema correctly validates CreateBatchFormData.
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CreateBatchFormData>({
    resolver: yupResolver(createValidationSchema(flowType)) as Resolver<CreateBatchFormData>,
    defaultValues: {
      batchId: DEFAULT_FORM_VALUES.batchId,
      billingEntity: DEFAULT_FORM_VALUES.billingEntity,
      supplier: DEFAULT_FORM_VALUES.supplier,
      brand: DEFAULT_FORM_VALUES.brand,
      salesAgent: DEFAULT_FORM_VALUES.salesAgent,
      priority: DEFAULT_FORM_VALUES.priority,
      clientReference: DEFAULT_FORM_VALUES.clientReference,
      farmerId: seed.farmerId ?? DEFAULT_FORM_VALUES.farmerId,
      farmId: DEFAULT_FORM_VALUES.farmId,
      labInstructions: DEFAULT_FORM_VALUES.labInstructions,
    },
  });

  // Watch values for conditional logic
  const selectedFarmerId = watch('farmerId');
  const selectedBatchId = watch('batchId');

  // Fetch farms when farmer changes (both NewRequest and LabTriggered flows)
  useEffect(() => {
    if ((flowType === 'NewRequest' || flowType === 'LabTriggered') && active && selectedFarmerId) {
      // Clear farm selection when farmer changes…
      setValue('farmId', null);
      // …except the farm seeded from the URL for THIS farmer, applied once the farms-by-farmer list has
      // settled (so the Autocomplete can show it). The seed is consumed only when it is actually APPLIED —
      // React StrictMode runs this effect twice on mount and cancels the first fetch, so consuming it up
      // front would lose the farm. A different farmer discards it.
      const seeded = pendingSeedFarm.current;
      const seededFarmId = seeded && seeded.farmerId === selectedFarmerId ? seeded.farmId : null;
      if (seeded && !seededFarmId) pendingSeedFarm.current = null;
      let cancelled = false;
      fetchFarmsByFarmer(selectedFarmerId)
        .then(() => {
          if (cancelled || !seededFarmId) return;
          setValue('farmId', seededFarmId);
          pendingSeedFarm.current = null;
        })
        .catch(() => undefined);
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [selectedFarmerId, flowType, active, fetchFarmsByFarmer, setValue]);

  // Pre-fill entity assignments when a batch is selected in ExistingBatch flow
  useEffect(() => {
    if (flowType !== 'ExistingBatch' || !active || !selectedBatchId) return;
    const batch = data.batches.find((b) => b.id === selectedBatchId);
    if (!batch) return;
    if (batch.farmer) {
      setValue('billingEntity', {
        entityType: 'Farmer',
        entityId: batch.farmer.idFarmer,
        entityName: `${batch.farmer.firstName} ${batch.farmer.surname || ''}`.trim(),
        email: batch.farmer.emailAddress || undefined,
        isSageReady: false,
      });
    }
    if (batch.lab) {
      setValue('supplier', {
        entityType: 'Lab',
        entityId: batch.lab.idLab,
        entityName: batch.lab.labName,
        email: batch.lab.emailAddress || undefined,
        isSageReady: false,
      });
    }
  }, [flowType, active, selectedBatchId, data.batches, setValue]);

  // Reset form when deactivated (dialog closed or page unmounted)
  useEffect(() => {
    if (!active) {
      reset();
      resetData();
      setError(null);
      setFlowType(DEFAULT_WORKFLOW_FLOW_TYPE);
      setDuplicates([]);
      setShowDuplicateWarning(false);
      setPendingFormData(null);
    }
  }, [active, reset, resetData]);

  // Handle flow type change - resets flow-specific fields
  const handleFlowTypeChange = useCallback((newFlowType: WorkflowFlowType) => {
    setFlowType(newFlowType);
    setValue('batchId', null);
    setValue('farmerId', null);
    setValue('farmId', null);
    setValue('clientReference', '');
    setValue('labInstructions', '');
    setError(null);
  }, [setValue]);

  // Build the DTO from form data (shared between onSubmit and confirmCreate)
  const buildDto = useCallback((formData: CreateBatchFormData): CreateBatchWorkflowDto => {
    const dto: CreateBatchWorkflowDto = {
      flowType,
      billingEntityType: formData.billingEntity?.entityType,
      billingEntityId: formData.billingEntity?.entityId,
      supplierEntityType: formData.supplier?.entityType,
      supplierEntityId: formData.supplier?.entityId,
      // Optional Brand (Partner) — omitted when no brand selected; backend treats absent as "no brand"
      brandEntityType: formData.brand?.entityType,
      brandEntityId: formData.brand?.entityId,
      // Optional Sales Agent (Agent) — omitted when none selected
      salesAgentEntityType: formData.salesAgent?.entityType,
      salesAgentEntityId: formData.salesAgent?.entityId,
      priority: formData.priority,
    };

    if (flowType === 'ExistingBatch') {
      dto.batchId = formData.batchId;
      if (formData.slaExpectedDate) {
        dto.slaExpectedDate = formData.slaExpectedDate.toISOString();
      }
    } else {
      dto.batchId = null;
      dto.clientReference = formData.clientReference || undefined;
      dto.farmerId = formData.farmerId || undefined;
      dto.farmId = formData.farmId || undefined;
      dto.labInstructions = formData.labInstructions || undefined;
      if (formData.slaExpectedDate) {
        dto.slaExpectedDate = formData.slaExpectedDate.toISOString();
      }
    }

    return dto;
  }, [flowType]);

  // Execute workflow creation (called directly or after duplicate warning)
  const executeCreate = useCallback(async (formData: CreateBatchFormData) => {
    setSubmitting(true);
    setError(null);
    try {
      const dto = buildDto(formData);
      const workflow = await workflowService.create(dto);
      enqueueSnackbar('Workflow created successfully', { variant: 'success' });
      onCreated?.(workflow);
      return workflow;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create workflow';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
      return undefined;
    } finally {
      setSubmitting(false);
    }
  }, [buildDto, enqueueSnackbar, onCreated]);

  // Handle "Create Anyway" from duplicate warning dialog
  const confirmCreateDespiteDuplicates = useCallback(async () => {
    setShowDuplicateWarning(false);
    if (pendingFormData) {
      await executeCreate(pendingFormData);
      setPendingFormData(null);
    }
  }, [pendingFormData, executeCreate]);

  // Handle cancel from duplicate warning dialog
  const cancelDuplicateWarning = useCallback(() => {
    setShowDuplicateWarning(false);
    setPendingFormData(null);
    setDuplicates([]);
  }, []);

  // Handle form submission - checks for duplicates before creating
  const onSubmit = useCallback(async (formData: CreateBatchFormData) => {
    // Validate farmer/farm for NewRequest only; LabTriggered and ExistingBatch defer entity resolution
    if (flowType === 'NewRequest' && !formData.farmerId) {
      enqueueSnackbar('Please select a farmer', { variant: 'warning' });
      return;
    }
    if (flowType === 'NewRequest' && !formData.farmId) {
      enqueueSnackbar('Please select a farm', { variant: 'warning' });
      return;
    }

    // Skip duplicate check for LabTriggered and ExistingBatch flows
    if (flowType !== 'LabTriggered' && flowType !== 'ExistingBatch' && (formData.farmerId || formData.farmId)) {
      try {
        const matches = await workflowService.checkDuplicates(
          formData.farmerId,
          formData.farmId,
        );
        if (matches.length > 0) {
          setDuplicates(matches);
          setPendingFormData(formData);
          setShowDuplicateWarning(true);
          return;
        }
      } catch {
        // Duplicate check is advisory — if it fails, proceed with creation
      }
    }

    return executeCreate(formData);
  }, [flowType, enqueueSnackbar, executeCreate]);

  return {
    // Form state
    flowType,
    handleFlowTypeChange,
    control,
    handleSubmit,
    errors,
    // Form data
    data,
    loading,
    // Watched values
    selectedFarmerId,
    // Submission
    submitting,
    error,
    onSubmit,
    // Duplicate warning
    duplicates,
    showDuplicateWarning,
    confirmCreateDespiteDuplicates,
    cancelDuplicateWarning,
    // Entity creation
    setValue,
    getValues,
    addFarmer,
    addFarm,
  };
}
